import {
  App,
  Button,
  Empty,
  Form,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  setupApi,
  type ClassSection,
} from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const yearsKey = ['setup', 'academic-years'] as const;
const classesKey = ['setup', 'classes', 'active'] as const;
const sectionsKey = ['setup', 'lookups', 'sections', 'active'] as const;
const csKey = (yearId: number) => ['setup', 'class-sections', yearId] as const;

interface FormShape {
  classId: number;
  sectionIds: number[];
}

export function ClassSectionsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('SETUP:CREATE'));
  const canDelete = useAuthStore((s) => s.hasAuthority('SETUP:DELETE'));
  const storeYearId = useAuthStore((s) => s.academicYearId);

  const [yearId, setYearId] = useState<number | null>(storeYearId);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data: years } = useQuery({
    queryKey: yearsKey,
    queryFn: setupApi.listAcademicYears,
  });

  useEffect(() => {
    if (yearId == null && years && years.length > 0) {
      const current = years.find((y) => y.current) ?? years[0];
      if (current) setYearId(current.id);
    }
  }, [yearId, years]);

  const { data: classes } = useQuery({
    queryKey: classesKey,
    queryFn: () => setupApi.listClasses(true),
  });

  const { data: sections } = useQuery({
    queryKey: sectionsKey,
    queryFn: () => setupApi.listLookup('sections', true),
  });

  const { data: rows, isLoading } = useQuery({
    queryKey: yearId != null ? csKey(yearId) : ['setup', 'class-sections', 'pending'],
    queryFn: () => setupApi.listClassSections(yearId!),
    enabled: yearId != null,
  });

  const create = useMutation({
    mutationFn: (values: FormShape) =>
      setupApi.bulkCreateClassSections(yearId!, values.classId, values.sectionIds),
    onSuccess: (created) => {
      message.success(`Added ${created.length} section${created.length === 1 ? '' : 's'}`);
      qc.invalidateQueries({ queryKey: csKey(yearId!) });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
      setOpen(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: setupApi.deleteClassSection,
    onSuccess: () => {
      message.success('Pairing removed');
      qc.invalidateQueries({ queryKey: csKey(yearId!) });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const columns: TableProps<ClassSection>['columns'] = [
    {
      title: 'Class',
      dataIndex: 'className',
      sorter: (a, b) => a.classSortOrder - b.classSortOrder,
      defaultSortOrder: 'ascend',
      render: (n: string) => <span style={{ fontWeight: 600 }}>{n}</span>,
    },
    {
      title: 'Section',
      dataIndex: 'sectionName',
      width: 160,
      render: (n: string) => <Tag color="blue">{n}</Tag>,
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canDelete ? (
          <Popconfirm
            title="Remove this pairing?"
            description="Existing enrollments tied to it will block removal."
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={() => remove.mutate(row.id)}
          >
            <Button size="small" danger type="text" icon={<DeleteOutlined />}>
              Remove
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  const yearOptions = (years ?? []).map((y) => ({
    value: y.id,
    label: y.current ? `${y.name} · current` : y.name,
  }));

  const selectedYear = years?.find((y) => y.id === yearId);
  const selectedClassId = Form.useWatch('classId', form);
  const usedSectionIds = new Set(
    (rows ?? []).filter((r) => r.classId === selectedClassId).map((r) => r.sectionId),
  );
  const sectionOptions = (sections ?? []).map((s) => ({
    value: s.id,
    label: s.name,
    disabled: usedSectionIds.has(s.id),
  }));

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Setup / Academic"
        title="Class × Section"
        description="Sections offered for each class within a given academic year."
        count={{ value: rows?.length ?? 0, label: 'pairings' }}
        actions={
          <Space>
            <Select
              value={yearId ?? undefined}
              onChange={setYearId}
              options={yearOptions}
              style={{ minWidth: 180 }}
              placeholder="Select year"
            />
            {canCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={yearId == null}
                onClick={() => {
                  form.resetFields();
                  setOpen(true);
                }}
              >
                Add sections
              </Button>
            )}
          </Space>
        }
      />

      {yearId == null ? (
        <Empty description="No academic years defined yet" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isLoading}
          columns={columns}
          dataSource={rows ?? []}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description={
                  selectedYear
                    ? `No sections assigned for ${selectedYear.name} yet`
                    : 'No data'
                }
              />
            ),
          }}
        />
      )}

      <Modal
        title={`Add sections${selectedYear ? ` to ${selectedYear.name}` : ''}`}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        okText="Add"
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item
            label="Class"
            name="classId"
            rules={[{ required: true, message: 'Pick a class' }]}
          >
            <Select
              placeholder="Select class"
              options={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            label="Sections"
            name="sectionIds"
            rules={[{ required: true, message: 'Pick at least one section' }]}
            tooltip="Sections already assigned to the chosen class are disabled."
          >
            <Select
              mode="multiple"
              placeholder="Select sections"
              options={sectionOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
